import { withDependencies, named } from '@wix/thunderbolt-ioc'
import type {
	BrowserWindow,
	IHeadContent
} from '@wix/thunderbolt-symbols'
import {
	BrowserWindowSymbol,
	FeatureStateSymbol,
	HeadContentSymbol,
	PageFeatureConfigSymbol,
	PageTransitionsCompletedSymbol,
} from '@wix/thunderbolt-symbols'
import type { PageTransitionsDidMountFactory, PageTransitionData, PageTransitionsPageConfig } from './types'
import { name } from './symbols'
import { ScrollRestorationAPISymbol } from 'feature-scroll-restoration'

function doViewTransition(
	pageConfig: PageTransitionsPageConfig,
	pageRendered: Promise<PageTransitionData>,
	onTransitionStart: () => void,
	onTransitionComplete: (pageId: string) => void
) {
	let pageId: string

	const update = () =>
		pageRendered.then((transitionData: PageTransitionData) => {
			pageId = transitionData.pageId as string
		})

	const types = [pageConfig.transitionName]
	// @ts-expect-error
	const transition = document.startViewTransition({ update, types })

	transition.ready.then(onTransitionStart)
	transition.finished.then(() => {
		onTransitionComplete(pageId)
	})
}

const pageTransitionsDidMountFactory: PageTransitionsDidMountFactory = (
	pageConfig,
	featureState,
	pageTransitionsCompleted,
	scrollRestorationAPI,
	browserWindow: BrowserWindow,
	headContent: IHeadContent
) => {
	const supportsViewTransition = browserWindow ? 'startViewTransition' in browserWindow.document : false
	const useViewTransition = supportsViewTransition && pageConfig.shouldUseViewTransition
	const useCssFeature = pageConfig.useCssFeatureForViewTransitions

	return {
		name: 'pageTransitions',
		pageWillMount() {
			if (!useCssFeature) {
				const viewTransitionsStyle = `<style id="page-transitions">@view-transition {navigation: auto;types: ${pageConfig.transitionName}}</style>`
				headContent.setHead(viewTransitionsStyle)
			}
		},
		pageDidMount(pageId) {
			const state = featureState.get()

			const mountDone = state?.mountDoneHandler
			if (mountDone) {
				mountDone({
					transitionName: pageConfig.transitionName,
					pageId,
				})
			}

			if (state?.isFirstMount ?? true) {
				pageTransitionsCompleted.notifyPageTransitionsCompleted(pageId)
			}

			featureState.update((current) => ({
				...current,
				isFirstMount: false,
			}))
		},
		pageWillUnmount({ contextId }) {
			const state = featureState.get()

			if (useViewTransition) {
				const onTransitionStarting = () => {
					if (!scrollRestorationAPI.getScrollYHistoryState()) {
						scrollRestorationAPI.scrollToTop()
					}
				}
				const onTransitionComplete = (pageId: string) => {
					pageTransitionsCompleted.notifyPageTransitionsCompleted(pageId)
					if (scrollRestorationAPI.getScrollYHistoryState()) {
						scrollRestorationAPI.restoreScrollPosition()
					}
				}

				const pageMountPromise = new Promise((resolve) => {
					featureState.update((currentState) => {
						return {
							...currentState,
							mountDoneHandler: resolve,
						}
					})
				}) as Promise<PageTransitionData>

				doViewTransition(pageConfig, pageMountPromise, onTransitionStarting, onTransitionComplete)
			}

			// release propStore subscription
			state?.propsUpdateListenersUnsubscribers?.[contextId]?.()
			featureState.update((currentState) => {
				const propsUpdateListenersUnsubscribers = currentState?.propsUpdateListenersUnsubscribers ?? {}
				delete propsUpdateListenersUnsubscribers[contextId]
				return {
					...currentState,
					propsUpdateListenersUnsubscribers,
				}
			})
		},
	}
}

export const PageTransitionsDidMount = withDependencies(
	[
		named(PageFeatureConfigSymbol, name),
		named(FeatureStateSymbol, name),
		PageTransitionsCompletedSymbol,
		ScrollRestorationAPISymbol,
		BrowserWindowSymbol,
		HeadContentSymbol,
	],
	pageTransitionsDidMountFactory
)
